var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { StyleSheet, Pressable, ScrollView } from 'react-native';
import { Button, Iffy, Box, luxColors, Select, Hoverable, util, DISCLAIMER_MODE, Disclaimer, InformationMessage, DNAText, DNABox, DNAButton, DNAModal, } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import Preview from '../Preview/Preview';
import { PreAVCheck } from '../index';
import { isSupported as isTwilioSupported } from 'twilio-video';
import { isMobile, isIOS, isChrome, isTablet, isSafari, isAndroid } from 'react-device-detect';
import { AUDIO_SOURCE } from '@alucio/video';
import { useMediaQuery } from 'react-responsive';
const styles = StyleSheet.create({
    formContainer: {
        backgroundColor: colors['color-text-basic'],
        paddingHorizontal: 48,
        paddingVertical: 24,
        borderRadius: 8,
        maxWidth: 1024,
        width: '100%',
    },
    mobileFormContainer: {
        backgroundColor: 'white',
        width: '100%',
        height: '100%',
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 64,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
    },
    notSupportedMessage: {
        marginLeft: -75,
        marginRight: -75,
        maxWidth: '90vw',
        marginBottom: 40,
    },
    meetingRoomName: {
        paddingBottom: 38,
        fontWeight: '500',
        fontSize: 20,
        textAlign: 'center',
        color: luxColors.contentText.primary,
    },
    nameInput: {
        height: 48,
        paddingLeft: 10,
        borderColor: colors['color-primary-500'],
        borderWidth: 1,
        borderStyle: 'solid',
    },
    joinButton: {
        backgroundColor: colors['color-primary-500'],
        borderWidth: 0,
        borderRadius: 200,
        fontSize: 17,
        marginTop: 56,
        width: 377,
    },
    previewInstructionsContainer: {
        marginTop: 16,
        marginBottom: 16,
        height: 42,
    },
    footerText: {
        color: luxColors.subtitle.tertiary,
        fontSize: 14,
    },
    hoveredLink: {
        borderBottomWidth: 1,
        borderBottomColor: luxColors.subtitle.tertiary,
    },
    warnMessage: {
        top: 'calc(50% - 20px/2)',
        fontFamily: 'Lato',
        fontSize: 14,
        lineHeight: 20,
        color: colors['color-danger-500'],
        margin: '0px 40px',
    },
    modalMessageContainer: {
        position: 'absolute',
        left: '30%',
    },
    modalMessageBackground: {
        zIndex: 2,
        width: '100%',
        height: '100%',
        position: 'absolute',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
});
var MediaAccessState;
(function (MediaAccessState) {
    MediaAccessState[MediaAccessState["unknown"] = 0] = "unknown";
    MediaAccessState[MediaAccessState["prompted"] = 1] = "prompted";
    MediaAccessState[MediaAccessState["allowed"] = 2] = "allowed";
    MediaAccessState[MediaAccessState["denied"] = 3] = "denied";
    MediaAccessState[MediaAccessState["notAvailable"] = 4] = "notAvailable";
    MediaAccessState[MediaAccessState["notFound"] = 5] = "notFound";
})(MediaAccessState || (MediaAccessState = {}));
function FooterOptionText(props) {
    const { type, onPress } = props;
    const [hovered, setIsHovered] = useState(false);
    function toggleHover() {
        setIsHovered(!hovered);
    }
    function onSelect() {
        onPress(type);
    }
    return (React.createElement(Hoverable, { onHoverIn: toggleHover, onHoverOut: toggleHover },
        React.createElement(Pressable, { onPress: onSelect },
            React.createElement(DNABox, null,
                React.createElement(DNAText, { style: [styles.footerText, hovered && styles.hoveredLink] }, type.toString())))));
}
function AllowInfoModal(props) {
    const { closeModal, deniedMediaImage } = props;
    return (React.createElement(Box, { style: styles.modalMessageBackground },
        React.createElement(DNABox, { fill: true, appearance: "col", alignX: "center" },
            React.createElement(DNABox, { fill: true, appearance: "row", alignX: "center", alignY: "center" },
                React.createElement(DNAModal, null,
                    React.createElement(DNAModal.Body, null,
                        React.createElement(PreAVCheck, { mobileView: isMobile, deniedMediaImage: deniedMediaImage, deniedMedia: true })),
                    React.createElement(DNAModal.Confirmation, null,
                        React.createElement(DNAButton, { onPress: closeModal, status: "primary" }, "Close")))))));
}
function PreviewForm(props) {
    const { onSubmit, mobileView, audioFiles, audioAccessState, videoAccessState, submitLabel = 'Join Meeting' } = props;
    const audioOptions = [AUDIO_SOURCE.COMPUTER, AUDIO_SOURCE.PHONE];
    const audioAccess = (audioAccessState === MediaAccessState.allowed);
    const videoAccess = (videoAccessState === MediaAccessState.allowed);
    const isSmallScreen = useMediaQuery({ maxWidth: 850 });
    const INFORMATION_TEXT = {
        denied: 'You can only call in from your phone due to blocked access.',
        notAvailable: 'Your microphone is being used by another application. ' +
            'Please close all other apps that may be using your microphone.',
        notFound: 'We are unable to detect your microphone',
    };
    const [audioOptionValue, setAudioOptionValue] = useState((isIOS || !audioAccess)
        ? AUDIO_SOURCE.PHONE : AUDIO_SOURCE.COMPUTER);
    const [deviceSettings, setDeviceSettings] = useState({
        isAudioEnabled: audioAccess,
        isVideoEnabled: videoAccess,
        isPhoneAudio: (isIOS || !audioAccess),
    });
    const onJoinClicked = () => __awaiter(this, void 0, void 0, function* () {
        isMobile && document.documentElement.requestFullscreen && (yield document.documentElement.requestFullscreen());
        const settings = Object.assign({}, deviceSettings);
        onSubmit(settings);
    });
    useEffect(() => {
        if (audioAccessState === MediaAccessState.allowed) {
            onSettingsChanged('isPhoneAudio', false);
            onSettingsChanged('isAudioEnabled', true);
            // set selection of computer audio if we allowed access automatically
            setAudioOptionValue(audioOptions[0]);
        }
        else {
            onSettingsChanged('isPhoneAudio', true);
            onSettingsChanged('isAudioEnabled', false);
        }
    }, [audioAccessState]);
    useEffect(() => {
        if (videoAccessState === MediaAccessState.allowed) {
            onSettingsChanged('isVideoEnabled', true);
        }
        else {
            onSettingsChanged('isVideoEnabled', false);
        }
    }, [videoAccessState]);
    const onAudioOptionChanged = (index) => {
        const selectedValue = audioOptions[index.row];
        setAudioOptionValue(selectedValue);
        if (selectedValue === AUDIO_SOURCE.COMPUTER) {
            onSettingsChanged('isPhoneAudio', false);
            onSettingsChanged('isAudioEnabled', true);
        }
        else {
            onSettingsChanged('isPhoneAudio', true);
            onSettingsChanged('isAudioEnabled', false);
        }
    };
    const onSettingsChanged = (settingName, value) => {
        setDeviceSettings((current) => {
            const newSettings = Object.assign({}, current);
            newSettings[settingName] = value;
            return newSettings;
        });
    };
    return (React.createElement(DNABox, { appearance: "col" },
        React.createElement(DNABox, null,
            React.createElement(DNABox, { childFill: true, appearance: isSmallScreen ? 'col' : 'row', fill: true, wrap: "start", spacing: 'medium', style: util.mergeStyles(undefined, { width: '100%' }, [{ flexDirection: 'column-reverse' }, isSmallScreen]) },
                React.createElement(DNABox, { appearance: "col", style: { width: 390, height: 360 }, key: 0 },
                    React.createElement(DNABox, { alignX: "center" },
                        React.createElement(DNAText, { h5: true, style: { paddingBottom: 20 } }, "Audio")),
                    React.createElement(Iffy, { is: !isIOS },
                        React.createElement(DNABox, { appearance: "col", style: { marginBottom: 16 } },
                            React.createElement(Select, { value: audioOptionValue, style: { flex: 1 }, onSelect: nextIndex => { var _a; return onAudioOptionChanged((_a = nextIndex[0]) !== null && _a !== void 0 ? _a : nextIndex); }, disabled: deviceSettings.isPhoneAudio && !audioAccess },
                                React.createElement(Select.Item, { key: 0, title: "Use Computer Audio" }),
                                React.createElement(Select.Item, { key: 1, title: "Call In" })),
                            React.createElement(Iffy, { is: !audioAccess },
                                React.createElement(DNABox, { style: styles.previewInstructionsContainer },
                                    React.createElement(DNAText, { style: styles.warnMessage }, INFORMATION_TEXT[MediaAccessState[audioAccessState]]))),
                            React.createElement(Iffy, { is: audioOptionValue === AUDIO_SOURCE.PHONE || !audioAccess },
                                React.createElement(DNABox, { style: styles.previewInstructionsContainer },
                                    React.createElement(DNAText, { style: { color: luxColors.subtitle.tertiary } }, "Call-in Instructions will be provided once the host admits you. Thank you for your patience."))))),
                    React.createElement(Iffy, { is: isIOS },
                        React.createElement(DNAText, { style: { marginLeft: '4vw', marginRight: '3vw' }, status: "warning" }, "Safari has known audio issues. Please call in using phone audio. You will be prompted to dial-in after being admitted to the meeting.")),
                    React.createElement(Preview, { onSettingsChanged: onSettingsChanged, isPhoneAudio: deviceSettings.isPhoneAudio, mobileView: mobileView, isAudioPreview: true, audioFiles: audioFiles, audioAccessState: audioAccessState })),
                React.createElement(DNABox, { appearance: "col", key: 1 },
                    React.createElement(DNABox, { alignX: "center" },
                        React.createElement(DNAText, { h5: true, style: { paddingBottom: 20 } }, "Video")),
                    React.createElement(Preview, { onSettingsChanged: onSettingsChanged, isPhoneAudio: deviceSettings.isPhoneAudio, mobileView: mobileView, videoAccessState: videoAccessState })))),
        React.createElement(DNABox, { alignX: "center" },
            React.createElement(Button.Kitten, { nativeID: "join-to-the-meeting", testID: "join-meeting", style: styles.joinButton, onPress: onJoinClicked }, submitLabel))));
}
var FOOTER_OPTION;
(function (FOOTER_OPTION) {
    FOOTER_OPTION["PRIVACY_POLICY"] = "Privacy Policy";
    FOOTER_OPTION["TERMS_OF_USE"] = "Terms of Use";
})(FOOTER_OPTION || (FOOTER_OPTION = {}));
function AttendeeJoin(props) {
    const { meetingName, attendeeName, onJoinMeeting, mobileView, audioFiles, deniedMediaImage, audioAccessState, videoAccessState, submitLabel, showDisclaimer = true, } = props;
    const [disclaimer, setDisclaimer] = useState({ isVisble: false, mode: DISCLAIMER_MODE.Privacy });
    const [allowInfoModal, setAllowInfoModal] = useState(false);
    const isSmallScreenHeight = useMediaQuery({
        maxHeight: 620,
    });
    const isSmallScreen = useMediaQuery({ maxWidth: 850 });
    const isDesktopSupportedBrowser = (isChrome);
    const isDesktop = (!isMobile && !isTablet);
    const isIOSSafari = (isIOS && isSafari);
    const isAndroidChrome = (isAndroid && isChrome);
    const isSupported = (isTwilioSupported &&
        ((isDesktop && isDesktopSupportedBrowser) ||
            isIOSSafari ||
            isAndroidChrome));
    function onCloseDisclaimer() {
        setDisclaimer(Object.assign(Object.assign({}, disclaimer), { isVisble: false }));
    }
    function onPress(type) {
        if (type === FOOTER_OPTION.PRIVACY_POLICY) {
            setDisclaimer({
                isVisble: true,
                mode: DISCLAIMER_MODE.Privacy,
            });
        }
        else if (type === FOOTER_OPTION.TERMS_OF_USE) {
            setDisclaimer({
                isVisble: true,
                mode: DISCLAIMER_MODE.ToU,
            });
        }
    }
    const onSettingsSubmit = (settings) => {
        onJoinMeeting({
            attendeeName: attendeeName,
            deviceSettings: settings,
        });
    };
    const howToAllow = function () {
        setAllowInfoModal(true);
    };
    const onCloseHowToAllow = function () {
        setAllowInfoModal(false);
    };
    return (React.createElement(React.Fragment, null,
        allowInfoModal && (audioAccessState === MediaAccessState.denied ||
            videoAccessState === MediaAccessState.denied) &&
            React.createElement(AllowInfoModal, { closeModal: () => onCloseHowToAllow(), deniedMediaImage: deniedMediaImage }),
        disclaimer.isVisble && React.createElement(Disclaimer, { mode: disclaimer.mode, onClose: onCloseDisclaimer }),
        React.createElement(DNABox, { as: ScrollView, appearance: "col", fill: true, alignX: "center", alignY: isSmallScreenHeight ? 'start' : 'center' },
            React.createElement(DNABox, { appearance: "col", style: [util.mergeStyles(undefined, [styles.mobileFormContainer, mobileView], [styles.formContainer, !mobileView], [{ paddingTop: 20 }, !isSupported], [{ minWidth: '60%', width: 'auto' }, isSmallScreen])] },
                React.createElement(Iffy, { is: audioAccessState === MediaAccessState.denied || videoAccessState === MediaAccessState.denied },
                    React.createElement(InformationMessage, { variance: "danger", text: 'Your mic and/or camera access is blocked. Other participants will not be able to see or hear you.', actionButtonFunction: () => howToAllow(), actionButtonText: "Allow Access" })),
                React.createElement(DNAText, { style: util.mergeStyles(undefined, styles.meetingRoomName, [{ marginTop: 20 }, !isSupported]) }, meetingName),
                React.createElement(Iffy, { is: attendeeName && !isMobile },
                    React.createElement(PreviewForm, { onSubmit: onSettingsSubmit, mobileView: mobileView, audioFiles: audioFiles, audioAccessState: audioAccessState, videoAccessState: videoAccessState, submitLabel: submitLabel })),
                React.createElement(DNABox, { alignX: "center", style: { marginTop: 24 } },
                    React.createElement(Iffy, { is: showDisclaimer },
                        React.createElement(DNAText, { style: styles.footerText },
                            React.createElement(DNABox, { style: { marginRight: 8 } },
                                React.createElement(FooterOptionText, { onPress: onPress, type: FOOTER_OPTION.TERMS_OF_USE })),
                            "|",
                            React.createElement(DNABox, { style: { marginLeft: 8 } },
                                React.createElement(FooterOptionText, { onPress: onPress, type: FOOTER_OPTION.PRIVACY_POLICY })))))))));
}
export default AttendeeJoin;
//# sourceMappingURL=AttendeeJoin.js.map